// Downloading a quote's PDF, and telling the person what is happening while it builds.
//
// The file is rendered server-side from the customer-facing offer page, so a click is
// followed by seconds of nothing. Three screens offer the download - the send screen, the
// one-off email screen and the quick quote builder - and each had its own copy of the
// waiting state, so a spinner on the button was all the feedback any of them gave and the
// wait read as the app having stalled. The hook and the notice live here so all three say
// the same thing.

import { useState, useCallback } from 'react';

import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';

import { downloadQuotePdf, quotePdfFilename } from '../utils/downloadQuotePdf.js';

// Say that it is working, roughly how long for, and that nothing more is needed from them -
// otherwise the natural move is to click the button again.
const SAVING = 'Saving your changes — the PDF is built from the saved quote.';
const BUILDING = 'Building the PDF. This takes a few seconds — the download starts on its own.';
const BUILDING_AFTER_SAVE = 'Changes saved. Building the PDF — the download starts on its own.';
const DONE = 'PDF downloaded.';
const SAVE_FAILED = 'Could not save your changes. The PDF is built from the saved quote, so nothing was downloaded — check the quote for anything missing, save it, then try again.';
// A quote can refuse the save and still be worth downloading: only its owner may write to it, so
// on somebody else's quote the save is refused every time and the download used to stop with it —
// nobody but the owner could get the file at all. There is nothing of theirs to lose in that case,
// and where there is, saying so beats handing back nothing.
const SAVED_VERSION = 'Could not save first, so this is the version already saved — anything changed here since is not in the file.';

/**
 * @param saveBeforeDownload  optional async save; the server builds the PDF from the saved
 *                            quote, so an edit still sitting in the browser would be missing
 *                            from the file without it.
 * @param hasUnsavedChanges   whether there is anything to save - a save is not worth a round
 *                            trip, or a bumped modified time, when nothing has changed.
 */
function useQuotePdfDownload({ opportunity, opportunityId, accesstoken, minimized = false, saveBeforeDownload, hasUnsavedChanges = false }) {
    const [ downloadingPdf, setDownloadingPdf ] = useState(false);
    const [ pdfError, setPdfError ] = useState(null);
    const [ notice, setNotice ] = useState(null);

    const dismissNotice = useCallback(() => setNotice(n => (n ? { ...n, open: false } : null)), []);

    const downloadPdf = useCallback(async () => {
        setPdfError(null);
        setDownloadingPdf(true);

        // An edit made moments ago may not have reached the server yet: the auto-save waits
        // three seconds, and it does not run at all on a quote that is no longer a draft. The
        // PDF is rendered from what the server holds, so without this the file quietly comes
        // back without the change that was just made.
        let building = BUILDING;
        if (saveBeforeDownload && hasUnsavedChanges) {
            setNotice({ open: true, severity: 'info', message: SAVING, waiting: true });
            try {
                const saved = await saveBeforeDownload();
                if (saved === false) throw new Error(SAVE_FAILED);
                building = BUILDING_AFTER_SAVE;
            } catch (e) {
                // Carry on with what the server already holds, and say so. Refusing outright
                // meant a quote nobody but its owner could save was a quote nobody but its
                // owner could download.
                building = SAVED_VERSION;
            }
        }

        // A download that quietly leaves out an edit deserves more than the same blue notice.
        setNotice({ open: true, severity: building === SAVED_VERSION ? 'warning' : 'info', message: building, waiting: true });
        try {
            await downloadQuotePdf({
                opportunityId: opportunityId || opportunity?.id,
                accesstoken,
                minimized: minimized === true,
                filename: quotePdfFilename(opportunity),
            });
            setNotice({ open: true, severity: 'success', message: DONE, waiting: false });
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message || 'Could not build the PDF.';
            setPdfError(detail);
            // The button's tooltip carries this too, but a tooltip is only read by someone
            // who already suspects something went wrong.
            setNotice({ open: true, severity: 'error', message: detail, waiting: false });
        } finally {
            setDownloadingPdf(false);
        }
    }, [ opportunity, opportunityId, accesstoken, minimized, saveBeforeDownload, hasUnsavedChanges ]);

    return { downloadingPdf, pdfError, downloadPdf, notice, dismissNotice };
}

// The waiting message stays until the download resolves; the two that report a result clear
// themselves, since by then the person has what they came for.
function QuotePdfNotice({ notice, onClose }) {
    if (!notice) return null;
    return (
        <Snackbar
            open={notice.open}
            autoHideDuration={notice.waiting ? null : 6000}
            onClose={(event, reason) => {
                if (reason === 'clickaway') return;
                onClose();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert
                severity={notice.severity}
                variant='filled'
                onClose={notice.waiting ? undefined : onClose}
                sx={{
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: 500,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    minWidth: '320px',
                    alignItems: 'center',
                }}
            >
                <Box>
                    {notice.message}
                    {notice.waiting && (
                        <LinearProgress
                            color='inherit'
                            sx={{ mt: '6px', height: '2px', borderRadius: '2px', opacity: 0.5 }}
                        />
                    )}
                </Box>
            </Alert>
        </Snackbar>
    );
}

export { useQuotePdfDownload, QuotePdfNotice };
